import { useGLFlags } from '@group-link-one/gl-utils';
import { FeatureFlags } from './featureFlags.enum';
import type { Privileges } from './privileges.types';

interface UsePrivileges {
  pages: Privileges;
}

export const usePrivileges = (): UsePrivileges => {
  const { showFeature } = useGLFlags();

  const dashboardShow = showFeature(FeatureFlags.DASHBOARD);
  const businessUnitsShow = showFeature(FeatureFlags.BUSINESS_UNITS_MANAGEMENT);
  const businessUnitsDeleteShow = showFeature(FeatureFlags.BUSINESS_UNITS_DELETE);
  const whatsNewPageShow = showFeature(FeatureFlags.WHATS_NEW_PAGE);
  const whatsNewCreateShow = showFeature(FeatureFlags.WHATS_NEW_CREATE);
  const whatsNewEditShow = showFeature(FeatureFlags.WHATS_NEW_EDIT);
  const whatsNewSubmitShow = showFeature(FeatureFlags.WHATS_NEW_SUBMIT);
  const whatsNewApproveShow = showFeature(FeatureFlags.WHATS_NEW_APPROVE);
  const whatsNewDeleteShow = showFeature(FeatureFlags.WHATS_NEW_DELETE);
  const whatsNewApproveOwnShow = showFeature(FeatureFlags.WHATS_NEW_APPROVE_OWN);
  const whatsNewResetViewerShow = showFeature(FeatureFlags.WHATS_NEW_RESET_VIEWER);
  const signaturesShow = showFeature(FeatureFlags.EMPLOYEE_SIGNATURE_PAGE);
  const signaturesCreateShow = showFeature(FeatureFlags.EMPLOYEE_SIGNATURE_CREATE);
  const signaturesDownloadShow = showFeature(FeatureFlags.EMPLOYEE_SIGNATURE_DOWNLOAD);
  const signaturesSendEmailShow = showFeature(FeatureFlags.EMPLOYEE_SIGNATURE_SEND_EMAIL);
  const settingsUsersPageShow = showFeature(FeatureFlags.SETTINGS_USERS_PAGE);
  const settingsUsersCreateShow = showFeature(FeatureFlags.SETTINGS_USERS_CREATE);
  const settingsUsersEditShow = showFeature(FeatureFlags.SETTINGS_USERS_EDIT);
  const settingsUsersDeleteShow = showFeature(FeatureFlags.SETTINGS_USERS_DELETE);
  const settingsUsersManagePermissionsShow = showFeature(
    FeatureFlags.SETTINGS_USERS_MANAGE_PERMISSIONS,
  );
  const platformUsersPreferencesPageShow = showFeature(
    FeatureFlags.PLATFORM_USERS_PREFERENCES_PAGE,
  );
  const pagesPageShow = showFeature(FeatureFlags.PAGES_PAGE);
  const pagesCreateShow = showFeature(FeatureFlags.PAGES_CREATE);
  const pagesEditShow = showFeature(FeatureFlags.PAGES_EDIT);
  const pagesDeleteShow = showFeature(FeatureFlags.PAGES_DELETE);
  const toursPageShow = showFeature(FeatureFlags.TOURS_PAGE);
  const toursCreateShow = showFeature(FeatureFlags.TOURS_CREATE);
  const toursEditShow = showFeature(FeatureFlags.TOURS_EDIT);
  const toursSubmitShow = showFeature(FeatureFlags.TOURS_SUBMIT);
  const toursApproveShow = showFeature(FeatureFlags.TOURS_APPROVE);
  const toursDeleteShow = showFeature(FeatureFlags.TOURS_DELETE);
  const toursResetViewerShow = showFeature(FeatureFlags.TOURS_RESET_VIEWER);
  const optInFeaturesPageShow = showFeature(FeatureFlags.OPT_IN_FEATURES_PAGE);
  const optInFeaturesCreateShow = showFeature(FeatureFlags.OPT_IN_FEATURES_CREATE);
  const optInFeaturesEditShow = showFeature(FeatureFlags.OPT_IN_FEATURES_EDIT);
  const optInFeaturesDeleteShow = showFeature(FeatureFlags.OPT_IN_FEATURES_DELETE);
  /*
    Its own flag, not the survey one. `surveys_purge` controlling a button on an opt-in screen
    would be a name that lies to whoever reads it next — and the BFF permission behind each is
    different too (`opt_in_feature:purge` here, `survey:purge` there).
  */
  const optInPurgeShow = showFeature(FeatureFlags.OPT_IN_PURGE);

  const surveysPageShow = showFeature(FeatureFlags.SURVEYS_PAGE);
  const surveysCreateShow = showFeature(FeatureFlags.SURVEYS_CREATE);
  const surveysEditShow = showFeature(FeatureFlags.SURVEYS_EDIT);
  const surveysDeleteShow = showFeature(FeatureFlags.SURVEYS_DELETE);
  const surveysSubmitShow = showFeature(FeatureFlags.SURVEYS_SUBMIT);
  const surveysApproveShow = showFeature(FeatureFlags.SURVEYS_APPROVE);
  const surveysResultsShow = showFeature(FeatureFlags.SURVEYS_RESULTS);
  const surveysPurgeShow = showFeature(FeatureFlags.SURVEYS_PURGE);

  const pages: Privileges = {
    DASHBOARD: {
      PAGE: dashboardShow,
    },
    BUSINESS_UNITS: {
      PAGE: businessUnitsShow,
      CREATE: businessUnitsShow,
      DELETE: businessUnitsDeleteShow,
      WHATS_NEW: {
        PAGE: whatsNewPageShow,
        CREATE: whatsNewCreateShow,
        EDIT: whatsNewEditShow,
        SUBMIT: whatsNewSubmitShow,
        APPROVE: whatsNewApproveShow,
        DELETE: whatsNewDeleteShow,
        APPROVE_OWN: whatsNewApproveOwnShow,
        RESET_VIEWER: whatsNewResetViewerShow,
      },
      PAGES: {
        PAGE: pagesPageShow,
        CREATE: pagesCreateShow,
        EDIT: pagesEditShow,
        DELETE: pagesDeleteShow,
      },
      TOURS: {
        PAGE: toursPageShow,
        CREATE: toursCreateShow,
        EDIT: toursEditShow,
        SUBMIT: toursSubmitShow,
        APPROVE: toursApproveShow,
        DELETE: toursDeleteShow,
        RESET_VIEWER: toursResetViewerShow,
      },
      OPT_IN_FEATURES: {
        PAGE: optInFeaturesPageShow,
        CREATE: optInFeaturesCreateShow,
        EDIT: optInFeaturesEditShow,
        DELETE: optInFeaturesDeleteShow,
        PURGE: optInPurgeShow,
      },
      SURVEYS: {
        PAGE: surveysPageShow,
        CREATE: surveysCreateShow,
        EDIT: surveysEditShow,
        DELETE: surveysDeleteShow,
        SUBMIT: surveysSubmitShow,
        APPROVE: surveysApproveShow,
        RESULTS: surveysResultsShow,
        PURGE: surveysPurgeShow,
      },
    },
    SIGNATURES: {
      PAGE: signaturesShow,
      CREATE: signaturesCreateShow,
      DOWNLOAD: signaturesDownloadShow,
      SEND_EMAIL: signaturesSendEmailShow,
    },
    SETTINGS: {
      USERS: {
        PAGE: settingsUsersPageShow,
        CREATE: settingsUsersCreateShow,
        EDIT: settingsUsersEditShow,
        DELETE: settingsUsersDeleteShow,
        MANAGE_PERMISSIONS: settingsUsersManagePermissionsShow,
      },
    },
    PLATFORM_USERS: {
      PREFERENCES: {
        PAGE: platformUsersPreferencesPageShow,
      },
    },
  };

  return {
    pages,
  };
};
